import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useLocation } from "react-router-dom";
import { refreshAccessToken } from "../utils/api/tokenRefresh";
import { isPublicRoute } from "../utils/publicRoutes";

export const SessionExpirationPopup = () => {
    const [showPopup, setShowPopup] = useState(false);
    const [countdown, setCountdown] = useState(10);
    const location = useLocation();

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
    };


    // Check token validity on mount + interval. An expired/near-expiry
    // access token first tries a silent refresh (using the longer-lived
    // refresh token) — the countdown-to-logout popup only appears if that
    // refresh itself fails, i.e. the user's refresh token is also dead.
    useEffect(() => {
        const checkTokenValidity = async () => {
            const token = localStorage.getItem("token");

            if (!token) {
                setShowPopup(true);
                return;
            }

            try {
                const decoded = jwtDecode(token);
                const currentTime = Math.floor(Date.now() / 1000);
                // Refresh a bit before actual expiry so requests in-flight
                // around the boundary don't get caught with a dead token.
                const nearExpiry = decoded.exp - currentTime < 5 * 60;

                if (nearExpiry) {
                    const refreshed = await refreshAccessToken();
                    if (!refreshed) setShowPopup(true);
                }
            } catch (error) {
                console.error("Invalid token:", error);
                setShowPopup(true);
            }
        };

        // Skip popup on public routes
        if (isPublicRoute(location.pathname)) {
            setShowPopup(false);
            return;
        }

        checkTokenValidity();
        const interval = setInterval(checkTokenValidity, 60 * 1000);

        return () => clearInterval(interval);
    }, [location.pathname]);

    // Countdown + auto logout
    useEffect(() => {
        if (!showPopup) return;

        setCountdown(10); // reset countdown when popup shows
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    logout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [showPopup]);

    if (!showPopup) return null;

    return (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
            <div className="bg-white p-5 rounded shadow-lg text-center">
                <h2 className="text-lg font-semibold">Your session has expired</h2>
                <p>You will be logged out in {countdown} seconds.</p>
                <button
                    onClick={logout}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
                >
                    Re-Login Now
                </button>
            </div>
        </div>
    );
};
